import React from 'react';
import { InputBaseComponentProps } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { withTranslation, WithTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DefaultFormData, InputFormatter, InputType } from '../../local-types';
import { REGEX_EMAIL, REGEX_PHONE } from '../../utils/stringUtils';

interface FormInputProps extends DefaultFormData, WithTranslation {
    value: any;
    auto?: boolean;
    type?: InputType;
    inputProps?: InputBaseComponentProps;
    startAdornment?: JSX.Element;
    endAdornment?: JSX.Element;
    onBlurOnly?: boolean;
    format?: InputFormatter;
}

const FormInput = (props: FormInputProps): JSX.Element => {
    const classes = useStyles();
    const {
        t,
        value,
        auto,
        type,
        disabled,
        inputProps,
        startAdornment,
        endAdornment,
        hint,
        required,
        placeholder,
        id,
        name,
        label,
        handleChange,
        onBlurOnly,
        regex,
        format,
    } = props;

    const customProps: any = {};
    if (!auto) {
        customProps.value = format && value ? format(value, 0).value : (value ?? '');
    }

    if (regex === 'default') {
        if (type === 'tel') {
            const regexTel = REGEX_PHONE;
            if (!regexTel.test(value)) {
                customProps.error = true;
                customProps.helperText = t('error.phone');
            }
        }
        if (type === 'email') {
            const regexEmail = REGEX_EMAIL;
            if (!regexEmail.test(value)) {
                customProps.error = true;
                customProps.helperText = t('error.email');
            }
        }
    } else if (regex instanceof RegExp) {
        // Use regex directly as a RegExp object
        if (!regex.test(value)) {
            customProps.error = true;
            customProps.helperText = t('error.generic');
        }
    }

    const onChange = (e) => {
        if (format) {
            // Rewritten in place, so the re-render finds the same value and leaves the caret alone
            const input = e.target;
            const res = format(
                input.value,
                input.selectionStart ?? input.value.length,
                value,
                e.nativeEvent?.inputType,
            );
            input.value = res.value;
            input.setSelectionRange(res.caret, res.caret);
        }
        handleChange(e, null, !onBlurOnly);
    };

    return (
        <FormControl margin="dense" className={classes.flex}>
            <div className={classes.formLabel}>{label}</div>
            <TextField
                variant="outlined"
                id={id}
                color="secondary"
                type={type ? type : 'text'}
                inputProps={inputProps}
                InputProps={{
                    startAdornment: startAdornment ? (
                        <InputAdornment position="start">{startAdornment}</InputAdornment>
                    ) : null,
                    endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : null,
                }}
                placeholder={placeholder}
                name={name}
                className={classes.formInput}
                required={required}
                disabled={disabled}
                onChange={onChange}
                onBlur={(e) => (onBlurOnly ? onChange(e) : {})}
                {...customProps}
            />
            {hint}
        </FormControl>
    );
};

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        flex: {
            display: 'flex',
            flex: 'inherit',
        },
        formLabel: {
            fontSize: 12,
            color: theme.palette.primary.dark,
            fontWeight: 500,
        },
        formInput: {
            width: '100%',
            minWidth: 200,
            margin: 0 + 'px!important',
            marginTop: 7 + 'px!important',
        },
    }),
);

export default withTranslation(['rider', 'common'])(FormInput);
